import React, { useCallback, useEffect, useState } from 'react';
import useForm from '@utils/useForm';
import { useHistory } from 'react-router-dom';
import { useAvivaSelector, useAvivaDispatch } from '@redux/hook';
import { BasicDetailsForm } from '@models/MLTC/BasicDetails.model';
import { updatePlanSelection } from '@redux/slices/MLTC/step1Slice';
import { styled } from '@linaria/react';
import { css } from '@linaria/core';
import { isEmpty } from 'lodash';
import { updateBasicDetails } from '@redux/slices/MLTC/step1Slice';

import IconInfo from '@assets/icon/information-red.svg';
import IconClose from '@assets/icon/circle-cancel-red.svg';
import { useTheme, ITheme } from 'src/theme';
import { LeftGrid, RightGrid, FullGrid, EmptyGrid, ContainerGrid, Container } from '@components/LayoutGrid';
import Heading from '@components/Heading';
import RadioButtonGroup from '@components/RadioButtonGroup';
import RadioButton from '@components/RadioButton';
import Link from '@components/Link';
import CheckboxList from '@components/CheckboxList';
import Checkbox from '@components/Checkbox';
import Text from '@components/Text';
import Button from '@components/Button';
import Bullet from '@components/Bullet';
import Divider from '@components/Divider';
import { validate } from './service';
import Segment from '@components/Segment';
import Accordion from '@components/Accordion';
import ContainerBlock from '@components/ContainerBlock';
import Icon from '@components/Icon';
import Modal from '@components/Modal';
import { GAAviva } from '@utils/gaAviva';
import { UOB_CONTACT_URL } from '@static/config';

const CloseButton = styled.div`
		display: flex;
		font-weight: bold;
		cursor: pointer;
	`

const ResponsiveBlock = styled.div`
	@media screen and (min-width: 1281px) {
		display: none !important;
	}
`;

const customInlineLink = css`
	display: inline;
	line-break: anywhere;
`;

const customRadioButton = css`
  @media screen and (max-width: 767px) {
    .radio-item {
      width: 100%;
      &:nth-child(odd) {
        &:after{
          width: 175px;
          height: 1px;
          bottom: 0;
          left: 50%;
          right: unset;
          top: unset;
          transform: translateX(-50%);
        }
      }
    }
    .radio-hover {
      bottom: auto;
      right: 0;
      height: 50%;
      width: auto;
    }
    .radio-hover.o0 {
      transform: translateY(100%);
    }
    .radio-hover.o100 {
      transform: translateY(0);
    }
  }
`
const page = `Let's get started`;
const trackOnload = (): void => {
  const GA = GAAviva.getInstance();
  let { user, form, page } = GA.digitalData;
  form.name = "Let's get started";
  GA.trackPageLoad({ user, form, page });
}
const trackFormSubmmit = (): void =>  {
  const GA = GAAviva.getInstance();
  GA.trackFormSuccess({});
}


function LetsGetStarted() {
	console.log(`QnB ➤ PAGE ➤ Aviva - ${page}`);
	const theme: ITheme = useTheme();
	const history = useHistory();
	const dispatch = useAvivaDispatch();

	const campaignDetail = useAvivaSelector(state => state.loading.campaignDetail);
	const initValue = useAvivaSelector(state => state.mltcStep1.basicDetails);
	const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);


	const { handleChange, values, errors, formValid } = useForm(new BasicDetailsForm(initValue), () => { }, validate);

	const paramCC = campaignDetail?.campaignCode ? `?cc=${campaignDetail.campaignCode}` : '';
	const [isUOB, setIsUOB] = useState<boolean>(false);
  	const [isShowUOB, setIsShowUOB] = useState<boolean>(false);
	const gotoUOBContact = () => {
		window.open(UOB_CONTACT_URL, "_blank","noopener noreferrer");
	};

	const gotoContact = useCallback(() => {
    if (isUOB) {
      setIsShowUOB(true);
    } else {
      history.push('/mltc-r2/contact' + paramCC);
    }
	}, [history, isUOB, setIsUOB]);
	
	const onContinue = useCallback(() => {
		console.log(`QnB ➤ onContinue() ➤`, { values });
		dispatch(updateBasicDetails(values));
		history.push('/mltc-r2/basic-details' + paramCC)
		trackFormSubmmit();
	}, [dispatch, values]);

	const onChangeTooltip = useCallback(() => {
		setTooltipOpen(prev => !prev);
	}, []);

	const onChangeRadio = useCallback((event: any) => {
		handleChange(event);
		const { name, value } = event.target;
		console.log(`QnB ➤ onChangeRadio(event) ➤`, { name, value, event });
		dispatch(updatePlanSelection(value));
	}, [handleChange, dispatch]);


	useEffect(() => {
		console.log(
			'QnB ➤ useEffect() ➤',
			{
				'step1Slice ➤ basicDetails': initValue,
				'local ➤ values': values
			}
		);
		const isUob = campaignDetail && campaignDetail?.code == 'LUB1';
		setIsUOB(isUob)
	
		if (!isEmpty(initValue)) {
			onChangeRadio({ target: { name: 'scheme', value: initValue?.scheme } });
		}
		trackOnload();

	}, [initValue, campaignDetail, onChangeRadio]);


	return (
		<Container>
			<ContainerGrid>
				<FullGrid>
					<Heading margin="0 0 30px"> Let&apos;s get started... </Heading>
				</FullGrid>
				<LeftGrid>
					<Text margin="0 0 30px">If you hold a <Link text="CareShield Life" target="_blank" rel="noopener noreferrer" url={"https://www.careshieldlife.gov.sg/careshield-life/benefits.html"} /> or <Link text="ElderShield" target="_blank" rel="noopener noreferrer" url={"https://www.careshieldlife.gov.sg/eldershield/benefits.html"} /> policy, you can enhance your coverage with Singlife’s Singlife CareShield Standard or Singlife CareShield Plus for greater financial assurance.</Text>
					<form onSubmit={e => { e.preventDefault() }} noValidate>
						<Heading variant="label">
							Which government long-term care scheme do you currently hold?
							{!tooltipOpen && (<Link className="tooltip" icon={IconInfo} iconSize="md" text="" variant="button" onClick={onChangeTooltip} />)}
							{tooltipOpen && (<Link className="tooltip-visible" icon={IconClose} iconSize="md" text="" variant="button" onClick={onChangeTooltip} />)}
						</Heading>
						{tooltipOpen && (
							<ContainerBlock className="tooltip" padding={20} paddingTablet={20} margin="0 0 20px 0" background={theme.colors.grey[600]}>
								<Bullet>
									<Text>CareShield Life payout starts from S$600 per month (in 2020), for a lifetime.</Text>
								</Bullet>
								<Bullet>
									<Text>ElderShield payout is up to S$400 per month, for up to 72 months.</Text>
								</Bullet>
								<CloseButton onClick={onChangeTooltip}> <Icon name="cancel-red" className='close-red' size="xsm" /> <Text fontWeight="bold" color={theme.colors.primary}> &nbsp;Close</Text>  </CloseButton>
							</ContainerBlock>
						)}

						<RadioButtonGroup marginTablet="0 0 15px" margin="0 0 15px" value={values['scheme']} name="scheme" onChange={onChangeRadio} className={customRadioButton}>
							<RadioButton value="CareShield Life" text="CareShield Life"></RadioButton>
							<RadioButton value="ElderShield" text="ElderShield"></RadioButton>
						</RadioButtonGroup>
						{values['scheme'] === 'CareShield Life' && (
							<Segment hasBorderTop={true}>
								<Text margin="0 0 20px 0">All Singapore Citizens and Permanent Residents born on or after 1980 will be automatically covered under CareShield Life from 1 October 2020 or upon 30 years of age, whichever is later.</Text>
								<Text margin="0 0 20px 0">For Singaporeans born in 1979 or earlier, your participation in CareShield Life is optional, and you can choose to join CareShield Life from 6 November 2021, regardless of your age, as long as you do not have any pre-existing disability.</Text>
								<Text margin="0 0 20px 0">If you are born in between 1970 and 1979 (both inclusive), insured under ElderShield 400, and are not severely disabled, you will automatically be enrolled in CareShield Life from 1 December 2021 onwards.</Text>
								<Text margin="0">For more information on CareShield Life, visit <Link className={customInlineLink} text="www.careshieldlife.gov.sg/careshield-life/benefits.html" url={"https://www.careshieldlife.gov.sg/careshield-life/benefits.html"} target="_blank" rel="noopener noreferrer"/> .</Text>
							</Segment>
						)}
						{values['scheme'] === 'ElderShield' && (
							<Segment hasBorderTop={true}>
								<Text margin="0 0 20px 0">Until 2019, all Singapore Citizens and Permanent Residents are automatically enrolled in ElderShield at age 40, unless you had opted out.</Text>
								<Text margin="0">For more information on ElderShield, visit <Link className={customInlineLink} text="www.careshieldlife.gov.sg/eldershield/benefits.html" url={"https://www.careshieldlife.gov.sg/eldershield/benefits.html"} target="_blank" rel="noopener noreferrer"/> .</Text>
							</Segment>
						)}

						{!errors?.dob?.isValid && errors?.dob?.message && (
							<Bullet variant="error">{errors?.dob?.message}</Bullet>
						)}

						<Heading variant="sub" margin="30px 0 15px"> Please tick and confirm </Heading>
						<Divider margin="0 0 32px" />
						<CheckboxList>
							<Checkbox margin="0 0 30px" name="required1" checked={values['required1']} onChange={handleChange}>
								<Text> This application to purchase Singlife CareShield Standard or Singlife CareShield Plus is without any advice from a financial adviser representative, and that you take responsibility to ensure that this product is suitable to meet your long term care needs.</Text>
							</Checkbox>
							<Checkbox margin="0 0 30px" name="required2" checked={values['required2']} onChange={handleChange}>
								<Text> I am not an undischarged bankrupt.</Text>
							</Checkbox>
						</CheckboxList>

						<Divider size={1} margin="0 0 20px" />

						<Accordion title="Important notes" isExpanded>
							<ContainerBlock>
								<Bullet>
									<Text>
										{(campaignDetail?.code !== 'LCB1' && !isUOB) &&
											`Singlife CareShield Standard and Singlife CareShield Plus are also available via Singlife's appointed distributors and intermediaries. Product features offered and premiums are the same irrespective of the purchase channel, i.e. buy online here or via appointed financial adviser representatives.`}
										{isUOB &&
											'I understand that the product features offered and premium rates are the same irrespective of the purchase channel, i.e. buy online here or via our appointed intermediary - United Overseas Bank Limited (“UOB”) Bancassurance Specialist.'}
										{(campaignDetail?.code === 'LCB1') &&
											'I understand that the product features offered and premium rates are the same irrespective of the purchase channel, i.e. buy online here or via our appointed intermediary (CIMB Bank Berhad, Singapore Branch) Specialist.'}

										{(campaignDetail?.code !== 'LCB1' && !isUOB) && (<><br />
										If you wish to buy online without financial advice, please proceed.
										Alternatively you may visit our <Link text="website" variant="button" target="_blank" rel="noopener noreferrer" onClick={() => { gotoContact() }}  />
											&nbsp;to leave your contact details and call back request, and we will assign a financial adviser representative to you.
										</>)}
									</Text>
								</Bullet>
								<Bullet>
									Anyone who pays for, or is insured under Singlife CareShield Standard / Singlife CareShield Plus (Supplement) is not eligible for Additional Premium Support (APS) from the Government*. If you are currently receiving APS to pay for your MediShield and/ or CareShield Life premiums, and you choose to be insured under this Supplement policy, you will stop receiving APS.<br />This applies even if you are not the person paying for this Supplement policy. In addition, if you choose to be insured under this Supplement policy, the person paying for the Supplement will stop receiving APS, if he or she is currently receiving APS.
									<Heading margin="10px 0 10px" variant='desc'>
										*APS is for families who need assistance with MediShield Life and/or CareShield Life premiums, even after receiving premium subsidies and making use of MediSave to pay for these premiums.
									</Heading>
								</Bullet>
							</ContainerBlock>
						</Accordion>
						<Divider size={4} margin="0 0 20px" />
					</form>

					<Button margin="40px 0 40px" disabled={!formValid} label="Start application" onClick={onContinue} />
					<ResponsiveBlock>
						<ContainerBlock
							borderTop={`3px solid ${theme.colors.primary}`}
							paddingTablet="25px 30px"
							padding="22px 30px"
							background={theme.colors.grey[200]}
							margin="0 0 60px"
						>
							<Heading margin="0 0 15px" variant='smallTitle' color={theme.colors.grey[400]} fontWeight={600}>About CareShield Life</Heading>
							<Text margin="0 0 15px" color={theme.colors.grey[500]}>
								All Singapore Citizens and Permanent Residents born on or after 1980 will be automatically covered under CareShield Life from 1 October 2020 or upon 30 years of age, whichever is later.
							</Text>
							<Text margin="0 0 15px" color={theme.colors.grey[500]}>
								For Singaporeans born in 1979 or earlier, your participation in CareShield Life is optional, and you can choose to join CareShield Life from 6 November 2021, regardless of your age, as long as you do not have any pre-existing disability.
							</Text>
							<Text margin="0 0 15px" color={theme.colors.grey[500]}>
								If you are born in between 1970 and 1979 (both inclusive), insured under ElderShield 400, and are not severely disabled, you will automatically be enrolled in CareShield Life from 1 December 2021 onwards.
							</Text>
							<Text margin={0} color={theme.colors.grey[500]}>
								For more information on CareShield, visit <Link url='' text="www.careshieldlife.gov.sg/careshield-life/benefits.html" />
							</Text>
						</ContainerBlock>
					</ResponsiveBlock>

				</LeftGrid>
				<EmptyGrid></EmptyGrid>
				<RightGrid>
				</RightGrid>

				<Modal show={isShowUOB} onClose={() => { setIsShowUOB(false) }}>
          <Text margin={'0px 0 30px'}>You will be redirected to our appointed intermediary - United Overseas Bank Limited (“UOB”) site to submit the contact me form.</Text>
          <Button margin="0 0 20px" label="Proceed" onClick={gotoUOBContact} />
          <Button variant="secondary" label="Cancel" margin={0} onClick={() => { setIsShowUOB(false) }} />
        </Modal>
			</ContainerGrid>

		</Container>
	);
};

export default LetsGetStarted;
